import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../../../components/ui/Button/Button';
import Switch from '../../../../../components/ui/Switch/Switch';
import { assignSettingsEditor, getSettingsEditors, SettingsEditor } from '../../../../../crud/management_api/settings_delegation';
import { settingsMessages as copy, delegatedSettingsMessages } from '../../../../../configuration/settingsDelegation';
import DirectMenuSetting from '../../../ClientManagement/OnlineMenuManagement/components/DirectMenuSetting';

/** Explicit editor delegation remains in the existing superadmin-only page. */
export default function SettingsDelegationAdmin({ localId }: { localId: number }) {
  const [editors, setEditors] = useState<SettingsEditor[] | null>(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const controller = useRef<AbortController>();
  useEffect(() => {
    const request = new AbortController();
    controller.current = request;
    setEditors(null);
    setError(false);
    setSaving(false);
    getSettingsEditors(localId, request.signal).then(rows => {
      if (!request.signal.aborted) setEditors(rows);
    }).catch(() => { if (!request.signal.aborted) setError(true); });
    return () => { request.abort(); controller.current?.abort(); };
  }, [localId, reload]);
  const assign = async (editor: SettingsEditor, canEdit: boolean) => {
    if (saving || error) return;
    const request = new AbortController();
    controller.current = request;
    setSaving(true);
    try {
      await assignSettingsEditor(localId, editor, canEdit, request.signal);
      if (!request.signal.aborted) setReload(value => value + 1);
    } catch {
      if (!request.signal.aborted) { setError(true); setSaving(false); }
    }
  };
  return (
    <section aria-label={copy.editors}>
      <DirectMenuSetting localId={localId} feature="delegated_settings"
        copy={delegatedSettingsMessages} onSaved={() => setReload(value => value + 1)} />
      <h3>{copy.editors}</h3>
      <p>{copy.editorScope}</p>
      {!editors && !error && <p role="status">{copy.loading}</p>}
      {error && <>
        <p role="alert">{copy.unavailable}</p>
        <Button onClick={() => setReload(value => value + 1)}>{copy.reload}</Button>
      </>}
      {editors?.length === 0 && <p>{copy.noEditors}</p>}
      {editors?.map(editor => (
        <Switch key={editor.user_id} id={`settings-editor-${editor.user_id}`}
          label={`${copy.allowEdit}: ${editor.username} (${editor.user_id})`}
          checked={editor.can_edit} disabled={saving || error || (!editor.eligible && !editor.can_edit)}
          onChange={value => assign(editor, value)} />
      ))}
    </section>
  );
}
